import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "./useApi";
import { debounce } from "lodash";
import { INITIAL_SESSION_DATA } from "../constants/steps";
import { useAuth } from "./useAuth";
import { secureDraftService } from "../services/draft.service";
import { parseSessionError } from "../utils/sessionErrors";

export const useSession = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const { get, post, put } = useApi();
  const [activeStep, setActiveStep] = useState("consent");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actualSessionId, setActualSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(INITIAL_SESSION_DATA);
  const [isTerminating, setIsTerminating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);

  const isSessionReady = Boolean(actualSessionId);
  const { user } = useAuth();

  // Define step order for navigation
  const STEP_ORDER = ["consent", "intake", "mentalStatus"];

  // Initialize secure draft service
  useEffect(() => {
    if (user?.id && user?.token) {
      secureDraftService.initialize(user.id, user.token);
    }
  }, [user]);

  // Auto-save function with proper debouncing
  const autoSaveDraft = useCallback(
    debounce((formType, formData) => {
      if (actualSessionId && user?.id && formData) {
        secureDraftService.autoSaveDraft(actualSessionId, formType, formData);
        setLastAutoSave(new Date());
        console.log(`Draft auto-saved: ${formType}`);
      }
    }, 3000),
    [actualSessionId, user?.id]
  );

  // Handle form changes with auto-save
  const handleFormChange = useCallback((formType, formData) => {
    setSessionData(prev => ({
      ...prev,
      [formType]: {
        ...prev[formType],
        ...formData
      }
    }));

    setHasUnsavedChanges(true);

    // Trigger auto-save
    autoSaveDraft(formType, formData);
  }, [autoSaveDraft]);

  // Load existing drafts when session is ready
  const loadExistingDrafts = useCallback(async () => {
    if (!actualSessionId || !user?.id) return;

    try {
      // First try to restore any server-side drafts
      await secureDraftService.restoreDraftsFromServer(actualSessionId);

      const formTypes = ['consent', 'intake', 'mentalStatus'];
      const drafts = {};
      let hasDrafts = false;

      for (const formType of formTypes) {
        const draftData = secureDraftService.getDraft(actualSessionId, formType);
        if (draftData) {
          drafts[formType] = draftData;
          hasDrafts = true;
        }
      }

      if (hasDrafts) {
        setSessionData(prev => ({
          ...prev,
          ...drafts
        }));
        setHasUnsavedChanges(true);
        console.log('Loaded existing drafts:', Object.keys(drafts));
      }
    } catch (error) {
      console.warn('Failed to load existing drafts:', error);
    }
  }, [actualSessionId, user?.id]);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!appointmentId) {
          throw new Error("No appointment ID provided");
        }

        const endpoint = `/sessions/${appointmentId}/initialize`;
        const initResponse = await post(endpoint, {});

        if (initResponse.data?.status === "success") {
          const sessionData = initResponse.data.data;

          if (!sessionData._id) {
            console.error("🚨 API response is missing _id!", sessionData);
            return;
          }

          setSessionData((prevData) => ({
            ...prevData,
            ...sessionData,
            // Initialize nested objects if they're missing
            mentalStatusExam: sessionData.mentalStatusExam || {},
            intake: {
              ...(prevData.intake || {}),
              ...(sessionData.intake || {}),
              impactOnFunctioning: {
                ...(prevData.intake?.impactOnFunctioning || {}),
                ...(sessionData.intake?.impactOnFunctioning || {})
              },
              history: {
                ...(prevData.intake?.history || {}),
                ...(sessionData.intake?.history || {})
              }
            }
          }));

          setActualSessionId(sessionData._id);

          // Load any existing drafts after session is initialized
          setTimeout(() => {
            loadExistingDrafts();
          }, 100);
        } else {
          throw new Error("Failed to initialize session");
        }
      } catch (err) {
        console.error("❌ Session initialization error:", err);
        setError(parseSessionError(err, 'session'));
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      initializeSession();
    }
  }, [appointmentId, post, loadExistingDrafts]);

  // Periodic session updates (reduced frequency to avoid conflicts with drafts)
  useEffect(() => {
    if (!actualSessionId) return;

    const fetchSessionUpdates = async () => {
      try {
        const response = await get(`/sessions/${actualSessionId}`);
        if (response?.data?.status === "success" && response.data.data) {
          // Only update if NO unsaved changes AND no recent auto-save
          const timeSinceLastAutoSave = lastAutoSave ? Date.now() - lastAutoSave.getTime() : Infinity;
          const safeToUpdate = !hasUnsavedChanges && timeSinceLastAutoSave > 10000; // 10 seconds buffer
          if (safeToUpdate) {
            setSessionData((prevData) => ({
              ...prevData,
              ...response.data.data,
              // Always ensure nested objects are preserved
              intake: {
                ...(prevData.intake || {}),
                ...(response.data.data.intake || {}),
                impactOnFunctioning: {
                  ...(prevData.intake?.impactOnFunctioning || {}),
                  ...(response.data.data.intake?.impactOnFunctioning || {})
                },
                history: {
                  ...(prevData.intake?.history || {}),
                  ...(response.data.data.intake?.history || {})
                }
              },
              mentalStatusExam: {
                ...(prevData.mentalStatusExam || {}),
                ...(response.data.data.mentalStatusExam || {})
              }
            }));
          }
        }
      } catch (err) {
        console.warn("Failed to fetch session updates:", err);
      }
    };

    // Fetch updates less frequently to avoid conflicts
    const interval = setInterval(fetchSessionUpdates, 30 * 60 * 1000); // Every 30 minutes
    return () => clearInterval(interval);
  }, [actualSessionId, get, hasUnsavedChanges]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      autoSaveDraft.cancel();
    };
  }, [autoSaveDraft]);

  const handleTerminate = () => {
    setIsTerminating(true);
    setActiveStep("termination");
  };

  const isStepComplete = (step) => {
    if (!sessionData) return false;

    switch (step) {
      case "consent":
        return Boolean(
          sessionData.consent?.clientName &&
          sessionData.consent?.clientSignature &&
          sessionData.consent?.counselorSignature &&
          sessionData.consent?.date
        );

      case "intake":
        return Boolean(
          sessionData.intake?.presentingProblems?.length > 0 &&
          sessionData.intake?.impactOnFunctioning?.current &&
          sessionData.intake?.impactOnFunctioning?.past
        );

      case "mentalStatus":
        return Boolean(
          sessionData.mentalStatusExam &&
          Object.keys(sessionData.mentalStatusExam).length > 0 &&
          Object.values(sessionData.mentalStatusExam).some(value =>
            value !== null && value !== undefined && value !== ''
          )
        );

      default:
        return false;
    }
  };

  // Navigation helper functions
  const canNavigateToStep = useCallback((targetStep) => {
    const currentIndex = STEP_ORDER.indexOf(activeStep);
    const targetIndex = STEP_ORDER.indexOf(targetStep);

    // Can navigate if:
    // 1. Going to current step
    // 2. Going to previous step (always allowed)
    // 3. Going to next step if current is complete
    return targetStep === activeStep ||
      targetIndex < currentIndex ||
      (targetIndex === currentIndex + 1 && isStepComplete(activeStep));
  }, [activeStep, isStepComplete]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(activeStep);

    if (currentIndex > 0) {
      const previousStep = STEP_ORDER[currentIndex - 1];
      setActiveStep(previousStep);
      setError(null); // Clear any errors when navigating
    }
  }, [activeStep]);

  const goToNextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(activeStep);

    if (currentIndex < STEP_ORDER.length - 1 && isStepComplete(activeStep)) {
      const nextStep = STEP_ORDER[currentIndex + 1];
      setActiveStep(nextStep);
      setError(null); // Clear any errors when navigating
    }
  }, [activeStep, isStepComplete]);

  const navigateToStep = useCallback((targetStep) => {
    if (canNavigateToStep(targetStep)) {
      setActiveStep(targetStep);
      setError(null); // Clear any errors when navigating
      return true;
    }
    return false;
  }, [canNavigateToStep]);

  // Check if user can go back from current position
  const canGoBack = useCallback(() => {
    // Can go back if not on first step
    const currentIndex = STEP_ORDER.indexOf(activeStep);
    return currentIndex > 0;
  }, [activeStep]);

  const handleFormSubmit = async (formType, formData, options = {}) => {
    if (!actualSessionId) {
      setError("No active session found");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let endpoint = `/sessions/${actualSessionId}`;
      let payload = {};

      switch (formType) {
        case "consent":
          endpoint = `/sessions/${actualSessionId}/consent`;
          payload = {
            consent: { ...formData, signedAt: new Date().toISOString() }
          };
          break;

        case "intake":
          payload = {
            intake: { ...formData }
          };
          break;

        case "mentalStatus":
          payload = {
            mentalStatusExam: { ...formData }
          };
          break;

        default:
          payload = { [formType]: formData };
      }

      const response = await put(endpoint, payload);

      if (response?.data?.status === "success") {
        // Clear the draft after successful submission
        secureDraftService.clearDraft(actualSessionId, formType);
        setHasUnsavedChanges(false);

        // Show success message
        setSuccessMessage(`${formType.charAt(0).toUpperCase() + formType.slice(1)} form saved successfully`);
        setTimeout(() => setSuccessMessage(null), 3000);

        // Update session data with response
        setSessionData(prevData => {
          const newData = JSON.parse(JSON.stringify(prevData));

          if (formType === "intake" && response.data.data.intake) {
            newData.intake = {
              ...(newData.intake || {}),
              ...response.data.data.intake,
              impactOnFunctioning: {
                ...(newData.intake?.impactOnFunctioning || {}),
                ...(response.data.data.intake.impactOnFunctioning || {})
              },
              history: {
                ...(newData.intake?.history || {}),
                ...(response.data.data.intake.history || {})
              }
            };
          }

          if (formType === "mentalStatus" && response.data.data.mentalStatusExam) {
            newData.mentalStatusExam = {
              ...(newData.mentalStatusExam || {}),
              ...response.data.data.mentalStatusExam
            };
          }

          return {
            ...newData,
            ...response.data.data,
          };
        });

        // Auto-advance to next step (but don't force it)
        if (formType === "consent" && !options.preventAutoAdvance) {
          setTimeout(() => setActiveStep("intake"), 300);
        } else if (formType === "intake" && !options.preventAutoAdvance) {
          setTimeout(() => setActiveStep("mentalStatus"), 300);
        }
      }
    } catch (err) {
      console.error("❌ Form submission error:", err);
      setError(parseSessionError(err, formType));
    } finally {
      setLoading(false);
    }
  };

  // Determine initial active step based on completion status
  useEffect(() => {
    if (!activeStep || activeStep === "consent") {
      if (isStepComplete("consent") && !isStepComplete("intake")) {
        setActiveStep("intake");
      } else if (isStepComplete("consent") && isStepComplete("intake") && !isStepComplete("mentalStatus")) {
        setActiveStep("mentalStatus");
      }
    }
  }, [sessionData, activeStep]);

  const handleSaveAndContinue = async () => {
    if (!isStepComplete(activeStep)) {
      return;
    }

    try {
      setLoading(true);

      // First, save the current step data
      let payload = {};
      if (activeStep === 'mentalStatus') {
        payload = {
          mentalStatusExam: sessionData.mentalStatusExam || {},
          status: "in-progress"
        };
      } else {
        payload = {
          [activeStep]: sessionData[activeStep],
          status: "in-progress",
        };
      }

      const response = await put(`/sessions/${actualSessionId}`, payload);

      if (response?.data?.status === "success") {
        // Clear the draft for the current step after successful save
        secureDraftService.clearDraft(actualSessionId, activeStep);
        setHasUnsavedChanges(false);

        // If we're at the mentalStatus step, complete the session
        if (activeStep === "mentalStatus") {
          const completeResponse = await post(`/sessions/${actualSessionId}/complete`);

          if (completeResponse?.data?.status === "success") {
            setSuccessMessage("Session completed successfully!");

            // Clear all remaining drafts
            secureDraftService.clearMemoryDrafts();

            // Redirect to sessions list
            if (user?.role === "hod") {
              navigate("/hod/sessions");
            } else {
              navigate("/counselor/sessions");
            }
          }
        } else {
          // Use the navigation helper to move to next step
          goToNextStep();
        }
      }
    } catch (err) {
      console.error("❌ Save error:", err);
      setError(parseSessionError(err, activeStep));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave? Your progress will be lost."
      );
      if (!confirmLeave) return;
    }

    // Clear all drafts when canceling
    secureDraftService.clearMemoryDrafts();
    navigate("/counselor/sessions");
  }, [hasUnsavedChanges, navigate]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hasUnsavedChanges) {
        // Flush any pending saves before tab becomes hidden
        secureDraftService.flushPendingSaves();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasUnsavedChanges]);

  return {
    sessionData,
    loading,
    error,
    activeStep,
    setActiveStep,
    isTerminating,
    handleFormSubmit,
    handleSaveAndContinue,
    handleCancel,
    handleTerminate,
    isStepComplete,
    isSessionReady,
    actualSessionId,
    successMessage,
    // Draft-related exports
    handleFormChange,
    hasUnsavedChanges,
    lastAutoSave,
    autoSaveDraft,
    // New navigation exports
    canNavigateToStep,
    goToPreviousStep,
    goToNextStep,
    navigateToStep,
    canGoBack,
    STEP_ORDER
  };
};

export default useSession;
